<script lang="ts" setup>
import { computed, inject, resolveComponent } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import useButton from '../DTButton/useButton'
import { ButtonVariant } from '../DTButton/types'
import { DTFieldGroupProvider, DTFieldProvider } from '../../keys'
import type { FieldGroupProvider, FieldProvider } from '../../types/field'

type ButtonSize = 'xs' | 'sm' | 'md'

const props = withDefaults(
  defineProps<{
    to?: RouteLocationRaw
    size?: ButtonSize
    // @vue-macros/nuxt package provides betterDefine which enables ComplexTypes for variant below.
    variant?: Exclude<keyof typeof ButtonVariant, `link${string}`>
    disabled?: boolean
  }>(),
  { variant: ButtonVariant.default },
)

const { tag, bound, variantClass } = useButton({
  to: computed(() => props.to),
  disabled: computed(() => props.disabled),
  variant: computed(() => props.variant),
})

const computedComponent = computed(() => {
  if (tag.value === 'nuxt-link')
    return resolveComponent('nuxt-link')

  return tag.value
})

const variants = computed<string[]>(() => {
  switch (props.variant) {
    case ButtonVariant.default:
      return [
        'text-gray-600',
        'border',
        'border-gray-300',
        'bg-gray-100',
        'hover:bg-gray-50',
        'active:bg-gray-100',
      ]
    default:
      return [...variantClass.value]
  }
})

const groupProvider = inject<FieldGroupProvider | undefined>(DTFieldGroupProvider, undefined)
const labelProvider = inject<FieldProvider | undefined>(DTFieldProvider, undefined)
const sizeClass = computed(() => {
  const size
    = (props.size ?? groupProvider?.size ?? labelProvider?.size) as ButtonSize
  switch (size) {
    case 'xs':
    case 'sm':
      return ['py-2 px-3 text-xs']
    case 'md':
    default:
      return ['py-2.5 px-3.5 text-sm']
  }
})

const buttonClass = computed(() => {
  const base = [
    'rounded-lg',
    'flex',
    'items-center',
    'justify-center',
    'gap-2',
    'leading-tight',
    'text-left',
  ]
  return [...base, ...variants.value, sizeClass.value]
})
</script>

<template>
  <component
    :is="computedComponent"
    v-bind="bound"
    :class="buttonClass"
  >
    <slot />
  </component>
</template>
